'use strict';

import toGeoJSON from '@mapbox/togeojson';

import turf_distance from '@turf/distance';
import { lineString as turf_lineString } from '@turf/helpers';
import turf_simplify from '@turf/simplify';

const maxBy = ( arr, func ) => {
  const max = Math.max( ...arr.map( func ) );
  return arr.find( item => func( item ) === max );
};
const minBy = ( arr, func ) => {
  const min = Math.min( ...arr.map( func ) );
  return arr.find( item => func( item ) === min );
};

import Evented from '../../ui/modules/evented';

export default class MapUIInstance extends Evented {

  constructor( container ) {

    super();

    this.container = container;

    this.options = this.container.options;
    this.dom_container = document.getElementById( this.options.dom_id );
    this.dom_container.parentNode.classList.add( this.options.dom_id );

    this._altitude_unit = ( 'fr' === _wp.l ) ? 'm' : 'ft';
    this._distance_unit = ( 'fr' === _wp.l ) ? 'km' : 'mi';

  }

  prepare_elevation_data( data ) {

    const elevation_base_options = {

      _data: {},

      data: false,

      show_path: true,
      show_data_markers: true,
      show_path_tooltip: true,
      show_chart: true,
      show_stats: true,

      simplification_tolerance: 0.00001,

      section_title: { en: "Elevation and Statistics", fr: "Profil Altimétrique et Statistiques" },
      chart_title: { en: "Elevation profile", fr: "Profil altimétrique" },
      stats_html: {

        en:
        `<h4>Statistics</h4>
        <dl class=elevation-stats>
        <span class=elevation-distance><dt>Distance</dt><dd>%distance%</dd></span>
        <span class=elevation-min><dt>Low</dt><dd>%alt_min%</dd></span>
        <span class=elevation-max><dt>High</dt><dd>%alt_max%</dd></span>
        <span class=elevation-down><dt>Descent</dt><dd>%alt_down%</dd></span>
        <span class=elevation-up><dt>Ascent</dt><dd>%alt_up%</dd></span>
        </dl>`,

        fr:
        `<h4>Statistiques</h4>
        <dl class=elevation-stats>
        <span class=elevation-distance><dt>Distance</dt><dd>%distance%</dd></span>
        <span class=elevation-min><dt><abbr data-tooltip='Altitude Minimum'>Alt. Min.</abbr></dt><dd>%alt_min%</dd></span>
        <span class=elevation-max><dt><abbr data-tooltip='Altitude Maximum'>Alt. Max.</abbr></dt><dd>%alt_max%</dd></span>
        <span class=elevation-down><dt><abbr data-tooltip='Dénivelé Négatif Cumulé'>Dénivelé Négatif</abbr></dt><dd>%alt_down%</dd></span>
        <span class=elevation-up><dt><abbr data-tooltip='Dénivelé Positif Cumulé'>Dénivelé Positif</abbr></dt><dd>%alt_up%</dd></span>
        </dl>`

      },

      'tooltip_html': {
        en: "%distance% / Alt: %altitude%",
        fr: "%distance% / Alt : %altitude%"
      },

    };

    this.options.elevation = Object.assign( elevation_base_options, this.options.elevation );

    //

    data = toGeoJSON.kml( ( new DOMParser() ).parseFromString( data, 'text/xml' ) );

    const elevation_data = [];
    const elevation_data_mapping = [];

    let currentPoint = false;
    let previousPoint = false;

    let path_cumul_length = 0;
    const stats = {};

    data.features.forEach( ( feature ) => {

      // Path
      if ( 'LineString' === feature.geometry.type ) {

        for ( let i = 0, j = feature.geometry.coordinates.length; i < j; i++ ) {

          currentPoint = feature.geometry.coordinates[ i ];

          if ( false !== previousPoint ) {
            path_cumul_length += this.distance_to_local_unit( turf_distance( previousPoint, currentPoint, { units: 'kilometers' } ) * 1000 );
          }

          if ( undefined !== currentPoint[ 2 ] ) {

            elevation_data.push( [
              currentPoint[ 0 ],
              currentPoint[ 1 ],
              this.altitude_to_local_unit( currentPoint[ 2 ] ),
              path_cumul_length
            ] );

            elevation_data_mapping.push( [
              currentPoint[ 0 ],
              currentPoint[ 1 ],
              elevation_data_mapping.length
            ] );

            currentPoint[ 3 ] = path_cumul_length;

            previousPoint = currentPoint;

          }

        }

      }

    } );

    stats.length = path_cumul_length;
    stats.min = minBy( elevation_data.filter( e => e[ 2 ] ), e => e[ 2 ] );
    stats.max = maxBy( elevation_data.filter( e => e[ 2 ] ), e => e[ 2 ] );
    stats.up = elevation_data.filter( e => e[ 2 ] ).reduce( ( memo, value, index, list ) => {
      return ( 0 !== index && list[ index - 1 ][ 2 ] < value[ 2 ] ) ? ( memo + ( value[ 2 ] - list[ index - 1 ][ 2 ] ) ) : memo;
    }, 0 );
    stats.down = elevation_data.filter( e => e[ 2 ] ).reduce( ( memo, value, index, list ) => {
      return ( 0 !== index && list[ index - 1 ][ 2 ] > value[ 2 ] ) ? ( memo + ( list[ index - 1 ][ 2 ] - value[ 2 ] ) ) : memo;
    }, 0 );
    stats.diff = elevation_data[ elevation_data.length - 1 ][ 2 ] - elevation_data[ 0 ][ 2 ];

    const elevation_data_simplified_keys = turf_simplify( turf_lineString( elevation_data_mapping ), { tolerance: this.options.elevation.simplification_tolerance, highQuality: true } ).geometry.coordinates.map( e => e[ 2 ] );
    elevation_data_simplified_keys[ 0 ] = 0;

    const elevation_data_simplified = elevation_data_simplified_keys.map( ( value ) => {
      return elevation_data[ value ];
    } );

    _wp.c.d && console.debug( `Simplified elevation data from ${elevation_data.length} to ${elevation_data_simplified.length} points` );
    _wp.c.d && console.debug( `Elevation data stats`, stats );

    return {

      geojson: data,
      elevation_data: elevation_data,
      elevation_stats: stats,
      elevation_data_simplified: elevation_data_simplified

    };

  }

  // http://www.movable-type.co.uk/scripts/latlong.html
  // Return meters
  compute_distance_between( lat1, lng1, lat2, lng2 ) {

    const a = Math.sin( ( ( lat2 - lat1 ) * ( Math.PI / 180 ) ) / 2 ) * Math.sin( ( ( lat2 - lat1 ) * ( Math.PI / 180 ) ) / 2 ) + Math.cos( lat1 * ( Math.PI / 180 ) ) * Math.cos( lat2 * ( Math.PI / 180 ) ) * Math.sin( ( ( lng2 - lng1 ) * ( Math.PI / 180 ) ) / 2 ) * Math.sin( ( ( lng2 - lng1 ) * ( Math.PI / 180 ) ) / 2 );
    return this.distance_to_local_unit( 6378137 * 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) ) );

  }

  // Input must be meters
  // Will return meters or feet
  altitude_to_local_unit( value ) {
    return 'm' === this._altitude_unit ? value : value * 3.280839895;
  }

  altitude_to_global_unit( value ) {
    return 'm' === this._altitude_unit ? value : value / 3.280839895;
  }

  // Input must be meters
  // Will return meters or feet
  distance_to_local_unit( value ) {
    return 'km' === this._distance_unit ? value : value * 3.280839895;
  }

  // Input must be meters
  format_distance( value, toString, addUnit ) {

    toString = ( undefined !== toString ? toString : true );
    addUnit = ( undefined !== addUnit ? addUnit : true );

    // Feet to miles
    if ( 'en' === _wp.l ) {
      value *= 0.00018939394;
    }
    else {
      value /= 1000;
    }

    if ( toString ) {
      return value.toLocaleString( _wp.l, { maximumFractionDigits: 2 } ) + ( addUnit ? `\xA0${this._distance_unit}` : '' );
    }

    return value;

  }

  // Input must be meters
  format_altitude( value, toString, addUnit ) {

    toString = ( undefined !== toString ? toString : true );
    addUnit = ( undefined !== addUnit ? addUnit : true );

    if ( toString ) {
      return value.toLocaleString( _wp.l, { maximumFractionDigits: 0 } ) + ( addUnit ? `\xA0${this._altitude_unit}` : '' );
    }

    return value;

  }

  // OK, needs to take an array in
  // Could be an array of GEOJson objects
  // or an array of properties (old style)

  _locations_to_geojson( data ) {

    const _data = {
      'type': 'FeatureCollection',
      'features': [],
    };
    let i;

    data.forEach( ( location ) => {

      const is_array = Array.isArray( location );

      // Extract

      let name = is_array ? location[ 0 ] : location.properties.name;

      if ( ! is_array && location.hasOwnProperty( 'properties' ) && location.properties.name ) {
        for ( i in location.properties ) {
          if ( 0 === i.indexOf( 'name_' ) ) {
            if ( 'object' !== typeof name ) {
              name = {};
            }
            name[ i.substring( 'name_'.length ) ] = location.properties[ i ];
          }
        }
      }

      let type_data = is_array ? location[ 3 ] : location.properties.type;

      if ( 'object' === typeof type_data && Array.isArray( type_data ) ) {
        type_data = type_data.join( '' );
      }

      const lng = is_array ? location[ 2 ] : location.geometry.coordinates[ 0 ];
      const lat = is_array ? location[ 1 ] : location.geometry.coordinates[ 1 ];

      const min_zoom =
        is_array && ( 'number' === typeof location[ 4 ][ 0 ] ) ?
        location[ 4 ][ 0 ] :
        ( location.properties && location.properties.hasOwnProperty( 'min_zoom' ) ? parseFloat( location.properties.min_zoom ) : this.options.label_default_zoom_range[ 0 ] );

      const max_zoom = Math.min( (
        is_array && ( 'number' === typeof location[ 4 ][ 1 ] ) ?
        location[ 4 ][ 1 ] :
        ( location.properties && location.properties.hasOwnProperty( 'max_zoom' ) ? parseFloat( location.properties.max_zoom ) : this.options.label_default_zoom_range[ 1 ] ) ), this.options.label_default_zoom_range[ 1 ] ); // Max zoom is 22

      const extra_data = ( is_array && undefined !== location[ 5 ] && ( 'object' === typeof location[ 5 ] && null !== location[ 5 ] ) ? location[ 5 ] : {} );
      const in_bounds = ( extra_data.hasOwnProperty( 'in_bounds' ) ) ? extra_data.in_bounds : true;

      // Process

      let type = '';
      let no_icon = false;
      let position = 'bottom';
      const localrank = {
        'poi': 1,
        'city': 1,
        'city-sm': 3,
      };
      const geometry = {
        'type': 'Point',
        'coordinates': [ lng, lat ],
      };

      if ( null !== ( i = type_data.match( /^(^\S+)/i ) ) ) {
        type = i[ 0 ];
      }
      if ( null !== ( i = type_data.match( /([\s|\b])(xs|sm|md|lg|xl)/i ) ) ) {
        type += `-${i[ 2 ]}`;
      }
      if ( null !== ( i = type_data.match( /\bno-icon\b/i ) ) ) {
        no_icon = true;
      }
      // The text-anchors are inverted in Mapbox
      if ( null !== ( i = type_data.match( /\b(t|r|b|l)\b/i ) ) ) {
        position = { t: 'top', r: 'right', b: 'bottom', l: 'left' }[ i[ 0 ] ];
      }
      if ( location.hasOwnProperty( 'properties' ) && location.properties.hasOwnProperty( 'position' ) ) {
        position = location.properties.position;
      }

      // Recreate. Voila!

      _data.features.push( {

        'type': "Feature",
        'geometry': geometry,
        'properties': Object.assign( {}, {
          'name': ( 'object' === typeof( name ) ) ? ( name.en ) : ( name ),
          'name_en': ( 'object' === typeof( name ) ) ? ( name.en ) : ( name ),
          'name_fr': ( 'object' === typeof( name ) ) ? ( name.fr ) : ( name ),
          'type': type,
          'no_icon': no_icon,
          'icon_variant': '',
          'position': position,
          'localrank': ( localrank[ type ] ? localrank[ type ] : 4 ),
          'in_bounds': in_bounds,
          'min_zoom': min_zoom,
          'max_zoom': max_zoom,
        }, extra_data )

      } );

    } );

    _data.features.map( ( element ) => {

      element.properties.name = element.properties.name.replace( /<br\s?\/?>/g, ' ' ).replace( /&nbsp;/g, '\u00A0' );
      element.properties.name_en = element.properties.name_en.replace( /<br\s?\/?>/g, ' ' ).replace( /&nbsp;/g, '\u00A0' );
      element.properties.name_fr = element.properties.name_fr.replace( /<br\s?\/?>/g, ' ' ).replace( /&nbsp;/g, '\u00A0' );
      return element;

    } );

    _wp.c.d && console.debug( `_locations_to_geojson`, _data.features );

    return _data;

  }

  _extract_geojson_features_by_geotype( geojson, geotype ) {

    const res = {
      features: [],
      type: "FeatureCollection"
    };

    geojson.features.forEach( ( feature ) => {
      if ( geotype === feature.geometry.type ) {
        res.features.push( feature );
      }
    } );

    return res;

  }

  _extract_geojson_features_by_property( geojson, property, value ) {

    const res = {
      features: [],
      type: "FeatureCollection"
    };

    geojson.features.forEach( ( feature ) => {
      if ( feature.properties.hasOwnProperty( property ) && value === feature.properties[ property ] ) {
        res.features.push( feature );
      }
    } );

    return res;

  }


}
